import { useEffect, useMemo, useState } from "react";
import type { Vehicle } from "./useVehicleStream";
import {
  buildRouteProjection,
  type ProjectedStop,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";
import "./TokyoLCD.css";

// Tokyo JR East-style LCD platform display.
//
// Mimics the dual-row LCD signs above platforms at JR stations (Yamanote,
// Chuo-Sobu, etc): dark navy background, a colored line stripe across the
// top, two stacked arrival rows ("先発 / Next" brighter, "次発 / 2nd"
// dimmer), each with type / destination / departs-in / track columns, and
// a stylized track diagram of the approaching train below. A scrolling
// safety marquee runs across the footer.

const STALE_AFTER_SEC = 120;
const CRUISE_M_PER_S = 13.4;
const APPROACH_HORIZON_M = 25_000;
// How often to flip destination between Japanese and English (ms).
const LANG_FLIP_MS = 3000;
// How often to rotate to the next station when auto-rotate is on (ms).
const AUTO_ROTATE_MS = 25_000;

// Headsign translations (English -> katakana). Anything not in this map
// renders as 「<English>」 so the panel still looks "translated".
const HEADSIGN_KANA: Record<string, string> = {
  lynnwood: "リンウッド",
  "federal way": "フェデラルウェイ",
  "angle lake": "アングルレイク",
  redmond: "レッドモンド",
  "bellevue downtown": "ベルビュー",
  "south bellevue": "サウスベルビュー",
  "mercer island": "マーサーアイランド",
  northgate: "ノースゲート",
  "lynnwood city center": "リンウッドシティセンター",
};

// Common station translations. Best-effort — anything not listed falls
// back to the English name.
const STATION_KANA: Record<string, string> = {
  westlake: "ウェストレイク",
  "capitol hill": "キャピトルヒル",
  "university of washington": "ワシントン大学",
  "u district": "Uディストリクト",
  roosevelt: "ルーズベルト",
  northgate: "ノースゲート",
  "lynnwood city center": "リンウッドシティセンター",
  "sea-tac/airport": "シータック空港",
  "seatac/airport": "シータック空港",
  "tukwila int'l blvd": "タクウィラ国際大通り",
  "international district/chinatown": "インターナショナルディストリクト",
  "pioneer square": "パイオニアスクエア",
  "beacon hill": "ビーコンヒル",
  "mount baker": "マウントベイカー",
  "columbia city": "コロンビアシティ",
  othello: "オセロ",
  "rainier beach": "レーニアビーチ",
  "bellevue downtown": "ベルビュー",
  "downtown bellevue": "ベルビュー",
  "mercer island": "マーサーアイランド",
  "south bellevue": "サウスベルビュー",
  "angle lake": "アングルレイク",
  "federal way downtown": "フェデラルウェイ",
  lynnwood: "リンウッド",
  redmond: "レッドモンド",
  "stadium station": "スタジアム",
  "judkins park": "ジャドキンズパーク",
};

// Stations to feature in the auto-rotate carousel. We pick the ones a
// rider is most likely to actually be standing at.
const ROTATE_STATION_HINTS = [
  "westlake",
  "capitol hill",
  "university of washington",
  "international district",
  "sea-tac",
  "bellevue",
];

const SAFETY_MESSAGES: { ja: string; en: string }[] = [
  { ja: "白線の内側でお待ちください。", en: "Please stand behind the yellow line." },
  { ja: "駆け込み乗車はおやめください。", en: "Please do not rush onto the train." },
  { ja: "ドアにご注意ください。", en: "Please watch your step. Mind the closing doors." },
  { ja: "車内では携帯電話をマナーモードに設定してください。", en: "Please switch your mobile phone to silent mode." },
  { ja: "次の電車をお待ちください。", en: "Please wait for the next train." },
];

type Props = {
  vehicles: Vehicle[];
  connected: boolean;
  /** True when the parent is showing a historical (scrubbed) snapshot. */
  historical?: boolean;
};

type Train = {
  vehicleId: string;
  routeId: string;
  routeColor: string;
  routeShort: string;
  distM: number;
  ts: number;
  headsign: string;
};

type Approach = Train & {
  remainingM: number;
  etaSec: number;
};

type StopEntry = ProjectedStop & {
  routeId: string;
  routeShort: string;
  routeColor: string;
};

export function TokyoLCD({ vehicles, connected, historical = false }: Props) {
  const [projections, setProjections] = useState<RouteProjection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedStopId, setSelectedStopId] = useState<string>("");
  const [autoRotate, setAutoRotate] = useState(true);
  const [langTick, setLangTick] = useState(0);
  const [, setNow] = useState(() => Math.floor(Date.now() / 1000));

  // 1Hz tick for clock + countdown. Frozen in history view.
  useEffect(() => {
    if (historical) return;
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [historical]);

  // Language flip (JA/EN crossfade). Frozen in history so the scrubbed
  // panel doesn't keep mutating.
  useEffect(() => {
    if (historical) return;
    const id = window.setInterval(() => setLangTick((t) => t + 1), LANG_FLIP_MS);
    return () => window.clearInterval(id);
  }, [historical]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        projs.sort((a, b) =>
          a.shortName.localeCompare(b.shortName, undefined, { numeric: true }),
        );
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build a deduplicated stop list across all routes. Each stop carries
  // the (last) route it appears on so we can color the panel chrome.
  const allStops = useMemo<StopEntry[]>(() => {
    if (!projections) return [];
    const byId = new Map<string, StopEntry>();
    for (const p of projections) {
      for (const s of p.stops) {
        if (!byId.has(s.id)) {
          byId.set(s.id, {
            ...s,
            routeId: p.routeId,
            routeShort: p.shortName,
            routeColor: p.color,
          });
        }
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [projections]);

  // Pick a sensible default station once geometry loads.
  useEffect(() => {
    if (!selectedStopId && allStops.length > 0) {
      const preferred =
        allStops.find((s) => /westlake/i.test(s.name)) ??
        allStops.find((s) => /university of washington/i.test(s.name)) ??
        allStops[0];
      setSelectedStopId(preferred.id);
    }
  }, [allStops, selectedStopId]);

  // Stations to cycle through under auto-rotate.
  const rotateStations = useMemo(() => {
    const picked: StopEntry[] = [];
    const seen = new Set<string>();
    for (const hint of ROTATE_STATION_HINTS) {
      const match = allStops.find(
        (s) => s.name.toLowerCase().includes(hint) && !seen.has(s.id),
      );
      if (match) {
        picked.push(match);
        seen.add(match.id);
      }
    }
    return picked;
  }, [allStops]);

  // Auto-rotate timer.
  useEffect(() => {
    if (!autoRotate || historical || rotateStations.length === 0) return;
    const id = window.setInterval(() => {
      setSelectedStopId((cur) => {
        const idx = rotateStations.findIndex((s) => s.id === cur);
        const next = rotateStations[(idx + 1) % rotateStations.length];
        return next?.id ?? cur;
      });
    }, AUTO_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [autoRotate, historical, rotateStations]);

  const selectedStop = useMemo(
    () => allStops.find((s) => s.id === selectedStopId) ?? null,
    [allStops, selectedStopId],
  );

  // For each route the selected station appears on, compute approach
  // info in both directions and pick whichever direction has trains
  // approaching. Then merge across routes and sort by ETA — the two
  // soonest become 先発 / 次発.
  const { approaches, perStopRoute } = useMemo(() => {
    if (!projections || !selectedStop) {
      return { approaches: [] as Approach[], perStopRoute: null as RoutePanel | null };
    }
    const refSec = historical
      ? vehicles.reduce((m, v) => Math.max(m, v.ts || 0), 0) ||
        Math.floor(Date.now() / 1000)
      : Math.floor(Date.now() / 1000);

    let merged: (Approach & { dirSign: 1 | -1 })[] = [];
    let chosenPanel: RoutePanel | null = null;

    for (const proj of projections) {
      const stop = proj.stops.find((s) => s.id === selectedStop.id);
      if (!stop) continue;

      // Try both travel directions; pick whichever yields trains in the
      // approaching window. If both do (a true through-station with
      // service in both directions), prefer the direction with a closer
      // train.
      const trains = collectTrains(vehicles, proj, refSec);
      const fwd = approachesIn(trains, stop, +1);
      const rev = approachesIn(trains, stop, -1);
      const useFwd = pickDirection(fwd, rev);
      const dirSign: 1 | -1 = useFwd ? +1 : -1;
      const list = useFwd ? fwd : rev;

      for (const a of list) merged.push({ ...a, dirSign });

      if (!chosenPanel) {
        // Build the line strip context from this route. Orient stops so
        // the selected station sits on the right and upstream stops trail
        // off to the left, matching the JR LCD convention.
        const oriented =
          dirSign === +1 ? proj.stops.slice() : proj.stops.slice().reverse();
        const stationIdx = oriented.findIndex((s) => s.id === selectedStop.id);
        const upstream = oriented.slice(Math.max(0, stationIdx - 5), stationIdx);
        chosenPanel = {
          projection: proj,
          dirSign,
          station: stop,
          upstream,
          trains,
        };
      }
    }

    merged.sort((a, b) => a.etaSec - b.etaSec);
    return { approaches: merged, perStopRoute: chosenPanel };
  }, [projections, selectedStop, vehicles, historical]);

  if (error) {
    return (
      <div className="tlcd-root">
        <div className="tlcd-empty">経路情報の読み込みに失敗しました: {error}</div>
      </div>
    );
  }
  if (!projections) {
    return (
      <div className="tlcd-root">
        <div className="tlcd-empty">読み込み中… / Loading…</div>
      </div>
    );
  }

  const lineColor = perStopRoute?.projection.color ?? selectedStop?.routeColor ?? "#00B261";
  const routeShort = perStopRoute?.projection.shortName ?? selectedStop?.routeShort ?? "";
  const next = approaches[0];
  const second = approaches[1];

  return (
    <div className="tlcd-root">
      <div className="tlcd-controls">
        <label className="tlcd-control-label" htmlFor="tlcd-station">
          駅 / Station
        </label>
        <select
          id="tlcd-station"
          className="tlcd-select"
          value={selectedStopId}
          onChange={(e) => {
            setSelectedStopId(e.target.value);
            setAutoRotate(false);
          }}
        >
          {allStops.length === 0 && <option value="">読み込み中…</option>}
          {allStops.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} · {s.routeShort} Line
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`tlcd-rotate ${autoRotate ? "on" : "off"}`}
          onClick={() => setAutoRotate((v) => !v)}
          aria-pressed={autoRotate}
        >
          {autoRotate ? "● 自動切替 ON" : "○ 自動切替 OFF"}
        </button>
      </div>

      <div
        className="tlcd-card"
        style={
          {
            "--tlcd-line": lineColor,
            "--tlcd-jr-green": "#00B261",
          } as React.CSSProperties
        }
      >
        <div className="tlcd-bezel">
          <header className="tlcd-status">
            <div className="tlcd-line-stripe" aria-hidden />
            <div className="tlcd-line-info">
              <span className="tlcd-line-jp">
                {routeShort ? `${routeShort}号線` : "—"}
              </span>
              <span className="tlcd-line-en">
                {routeShort ? `${routeShort} Line` : ""} · 各駅停車 / Local
              </span>
            </div>
            <div className="tlcd-station-info">
              <span className="tlcd-station-jp">
                {selectedStop ? toKana(selectedStop.name, STATION_KANA) : ""}
              </span>
              <span className="tlcd-station-en">
                {selectedStop?.name ?? ""}
              </span>
            </div>
            <div className="tlcd-conn">
              <span className={`tlcd-conn-dot ${historical ? "hist" : connected ? "ok" : "wait"}`} />
              <span className="tlcd-conn-label">
                {historical ? "履歴 HIST" : connected ? "運行中 LIVE" : "待機 WAIT"}
              </span>
            </div>
          </header>

          <div className="tlcd-rows">
            <ArrivalRow
              kindJp="先発"
              kindEn="Next"
              tone="primary"
              approach={next}
              langTick={langTick}
            />
            <ArrivalRow
              kindJp="次発"
              kindEn="2nd"
              tone="secondary"
              approach={second}
              langTick={langTick}
            />
          </div>

          <TrackDiagram panel={perStopRoute} />

          <Marquee historical={historical} />

          <div className="tlcd-reflection" aria-hidden />
        </div>
      </div>
    </div>
  );
}

type RoutePanel = {
  projection: RouteProjection;
  dirSign: 1 | -1;
  station: ProjectedStop;
  upstream: ProjectedStop[];
  trains: Train[];
};

function ArrivalRow({
  kindJp,
  kindEn,
  tone,
  approach,
  langTick,
}: {
  kindJp: string;
  kindEn: string;
  tone: "primary" | "secondary";
  approach: Approach | undefined;
  langTick: number;
}) {
  const showJa = langTick % 2 === 0;
  const headsign = approach?.headsign ?? "";
  const kana = toKana(headsign, HEADSIGN_KANA);
  const trackNum = approach ? trackForRoute(approach) : "—";

  return (
    <div className={`tlcd-row tlcd-row-${tone}`}>
      <div className="tlcd-col tlcd-col-kind">
        <span className="tlcd-kind-jp">{kindJp}</span>
        <span className="tlcd-kind-en">{kindEn}</span>
      </div>
      <div className="tlcd-col tlcd-col-type">
        <span className="tlcd-type-jp">各駅停車</span>
        <span className="tlcd-type-en">Local</span>
      </div>
      <div className="tlcd-col tlcd-col-dest">
        {approach ? (
          <div className="tlcd-dest-stack">
            <span className={`tlcd-dest-line ja ${showJa ? "show" : "hide"}`}>
              {kana}
              <span className="tlcd-dest-yuki">ゆき</span>
            </span>
            <span className={`tlcd-dest-line en ${showJa ? "hide" : "show"}`}>
              For {headsign || "—"}
            </span>
          </div>
        ) : (
          <span className="tlcd-dest-empty">--- / ---</span>
        )}
      </div>
      <div className="tlcd-col tlcd-col-eta">
        <EtaReadout etaSec={approach?.etaSec} />
      </div>
      <div className="tlcd-col tlcd-col-track">
        <span className="tlcd-track-jp">{trackNum}番線</span>
        <span className="tlcd-track-en">Track {trackNum}</span>
      </div>
    </div>
  );
}

function EtaReadout({ etaSec }: { etaSec: number | undefined }) {
  if (etaSec == null || !Number.isFinite(etaSec)) {
    return (
      <div className="tlcd-eta">
        <span className="tlcd-eta-num">--</span>
        <span className="tlcd-eta-unit">分</span>
      </div>
    );
  }
  if (etaSec < 60) {
    return (
      <div className="tlcd-eta tlcd-eta-soon">
        <span className="tlcd-eta-soon-jp">まもなく</span>
        <span className="tlcd-eta-soon-en">Soon</span>
      </div>
    );
  }
  const mins = Math.max(1, Math.round(etaSec / 60));
  return (
    <div className="tlcd-eta">
      <span className="tlcd-eta-num">{mins}</span>
      <span className="tlcd-eta-unit">
        分<small>min</small>
      </span>
    </div>
  );
}

function TrackDiagram({ panel }: { panel: RoutePanel | null }) {
  if (!panel) {
    return <div className="tlcd-track-diagram empty" />;
  }
  const { projection, dirSign, station, upstream, trains } = panel;
  // Render upstream + station; rightmost = current platform.
  const allStops = [...upstream, station];
  if (allStops.length < 2) {
    return <div className="tlcd-track-diagram empty" />;
  }
  const origin = allStops[0].distM;
  const span = station.distM - origin; // signed; same sign as dirSign

  // Train glyph positions (only those upstream of station and within
  // the diagram's left edge).
  const visible = trains
    .map((t) => {
      const remainingM = (station.distM - t.distM) * dirSign;
      const frac = span === 0 ? 0 : (t.distM - origin) / span;
      return { ...t, remainingM, frac };
    })
    .filter((t) => t.frac >= -0.05 && t.frac <= 1.02 && t.remainingM > -200)
    .sort((a, b) => a.frac - b.frac);

  return (
    <div className="tlcd-track-diagram">
      <div className="tlcd-diagram-rail" aria-hidden>
        <div className="tlcd-rail-line" />
        {allStops.map((s, i) => {
          const isStation = s.id === station.id;
          const f = i / (allStops.length - 1);
          return (
            <div
              key={s.id}
              className={`tlcd-rail-stop ${isStation ? "is-current" : ""}`}
              style={{ left: `${f * 100}%` }}
            >
              <span className="tlcd-rail-dot" />
              <span className="tlcd-rail-stop-label">
                <span className="tlcd-rail-stop-jp">
                  {toKana(s.name, STATION_KANA)}
                </span>
                <span className="tlcd-rail-stop-en">{s.name}</span>
              </span>
              {isStation && <span className="tlcd-rail-arrow">▲</span>}
            </div>
          );
        })}
        {visible.map((t) => (
          <div
            key={t.vehicleId}
            className="tlcd-rail-train"
            style={{ left: `${Math.max(0, Math.min(1, t.frac)) * 100}%` }}
            title={`${t.vehicleId} → ${t.headsign}`}
          >
            <span className="tlcd-rail-train-box" />
          </div>
        ))}
      </div>
      <div className="tlcd-diagram-legend">
        <span className="tlcd-legend-arrow">←</span>
        <span className="tlcd-legend-text">
          進行方向 / Direction of travel · {projection.shortName} Line
        </span>
      </div>
    </div>
  );
}

function Marquee({ historical }: { historical: boolean }) {
  // Concatenate every safety message into one long string so the CSS
  // marquee scrolls through them without restart artifacts.
  const text = SAFETY_MESSAGES
    .map((m) => `${m.ja}    ${m.en}`)
    .join("    ✦    ");
  return (
    <div className="tlcd-marquee" aria-label="Safety announcements">
      <div className={`tlcd-marquee-track ${historical ? "paused" : ""}`}>
        <span className="tlcd-marquee-seg">{text}</span>
        <span className="tlcd-marquee-seg" aria-hidden>
          {text}
        </span>
      </div>
    </div>
  );
}

function collectTrains(
  vehicles: Vehicle[],
  proj: RouteProjection,
  nowSec: number,
): Train[] {
  const out: Train[] = [];
  for (const v of vehicles) {
    if (!v.vehicle_id || v.route_id !== proj.routeId) continue;
    const ts = v.ts || nowSec;
    if (nowSec - ts > STALE_AFTER_SEC) continue;
    const distM = proj.project(v.lat, v.lon);
    if (distM == null) continue;
    out.push({
      vehicleId: v.vehicle_id,
      routeId: proj.routeId,
      routeColor: proj.color,
      routeShort: proj.shortName,
      distM,
      ts,
      headsign: v.trip_headsign ?? "",
    });
  }
  return out;
}

function approachesIn(
  trains: Train[],
  station: ProjectedStop,
  dirSign: 1 | -1,
): Approach[] {
  return trains
    .map<Approach>((t) => {
      const remainingM = (station.distM - t.distM) * dirSign;
      return { ...t, remainingM, etaSec: remainingM / CRUISE_M_PER_S };
    })
    .filter((a) => a.remainingM > 0 && a.remainingM <= APPROACH_HORIZON_M)
    .sort((a, b) => a.remainingM - b.remainingM);
}

function pickDirection(fwd: Approach[], rev: Approach[]): boolean {
  if (fwd.length && !rev.length) return true;
  if (rev.length && !fwd.length) return false;
  if (!fwd.length && !rev.length) return true;
  return fwd[0].remainingM <= rev[0].remainingM;
}

// Faux track number derived from route + travel direction. JR platforms
// are typically 1-2 per line per direction, so we use {1,2} for the two
// directions of route 1 and {3,4} for route 2 to keep them distinct.
function trackForRoute(a: Approach & { dirSign?: 1 | -1 }): string {
  const base = /2/.test(a.routeShort) ? 3 : 1;
  const offset = a.dirSign === -1 ? 1 : 0;
  return String(base + offset);
}

function toKana(name: string, dict: Record<string, string>): string {
  if (!name) return "";
  const key = name.toLowerCase().trim();
  const direct = dict[key];
  if (direct) return direct;
  // Try a loose contains match — handles "Sea-Tac/Airport Station" etc.
  for (const k of Object.keys(dict)) {
    if (key.includes(k)) return dict[k];
  }
  return `「${name}」`;
}
